/* ============================================
   MAIN LAYOUT
   The Creative Orchestra - GEMINI Framework
   Default layout wrapper for all pages
   ============================================ */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Navigation from '@components/common/Navigation';
import Footer from '@components/common/Footer';
import AudioController from '@components/common/AudioController';
import ScrollToTop, { ScrollToTopButton } from '@components/common/ScrollToTop';
import { useTheme } from '@context/ThemeContext';

/**
 * Main Layout Component
 * Provides consistent structure with navigation, footer, and page transitions
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Page content
 * @param {boolean} props.showFooter - Whether to show footer (default: true)
 * @param {boolean} props.showNavigation - Whether to show navigation (default: true)
 * @param {boolean} props.fullHeight - Whether content should fill viewport height
 * @param {string} props.className - Additional CSS classes
 */
const MainLayout = ({
  children,
  showFooter = true,
  showNavigation = true,
  fullHeight = false,
  className = '',
}) => {
  const location = useLocation();
  const { activeTheme, isTransitioning } = useTheme();

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Page transition variants
  const pageVariants = {
    initial: {
      opacity: 0,
      y: 20,
    },
    animate: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.25, 0.46, 0.45, 0.94],
      },
    },
    exit: {
      opacity: 0,
      y: -20,
      transition: {
        duration: 0.3,
        ease: [0.25, 0.46, 0.45, 0.94],
      },
    },
  };

  return (
    <div 
      className={`
        min-h-screen flex flex-col
        bg-cosmos text-white
        ${fullHeight ? 'h-screen overflow-hidden' : ''}
        ${className}
      `}
      style={{
        background: activeTheme?.gradients?.backgroundDark || 
          'linear-gradient(180deg, #0a0a0f 0%, #0f1419 50%, #0a0a0f 100%)',
      }}
    >
      {/* Scroll to top utility */}
      <ScrollToTop />

      {/* Navigation */}
      {showNavigation && <Navigation />}

      {/* Audio Controller (floating) */}
      <AudioController />

      {/* Main Content with Page Transitions */}
      <AnimatePresence mode="wait">
        <motion.main
          key={location.pathname}
          variants={pageVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          className={`
            flex-grow
            ${showNavigation ? 'pt-20' : ''}
            ${fullHeight ? 'overflow-hidden' : ''}
          `}
        >
          {children}
        </motion.main>
      </AnimatePresence>

      {/* Footer */}
      {showFooter && !fullHeight && <Footer />}

      {/* Scroll to Top Button */}
      <ScrollToTopButton threshold={400} />

      {/* Theme transition overlay */}
      <AnimatePresence>
        {isTransitioning && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 pointer-events-none bg-black/10 backdrop-blur-sm"
          />
        )}
      </AnimatePresence>
    </div>
  );
};

/**
 * Minimal Layout - No navigation or footer
 * Used for immersive experiences like the landing page
 */
export const MinimalLayout = ({ children, className = '' }) => {
  const location = useLocation();
  const { activeTheme } = useTheme();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div 
      className={`min-h-screen ${className}`}
      style={{
        background: activeTheme?.gradients?.backgroundDark || 
          'linear-gradient(180deg, #0a0a0f 0%, #0f1419 50%, #0a0a0f 100%)',
      }}
    >
      <ScrollToTop />
      <AudioController />
      
      <AnimatePresence mode="wait">
        <motion.div
          key={location.pathname}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
        >
          {children}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

/**
 * Reading Layout - Optimized for novel reading
 * Includes navigation and footer, optimized typography
 */
export const ReadingLayout = ({ children, className = '' }) => {
  const location = useLocation();
  const { activeTheme } = useTheme();

  return (
    <div 
      className={`min-h-screen flex flex-col ${className}`}
      style={{
        background: activeTheme?.gradients?.backgroundDark || 
          'linear-gradient(180deg, #0a0a0f 0%, #1a1510 50%, #0a0a0f 100%)',
      }}
    >
      <ScrollToTop />
      <Navigation />
      <AudioController />
      
      <AnimatePresence mode="wait">
        <motion.main
          key={location.pathname}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          className="flex-grow pt-20"
        >
          {children}
        </motion.main>
      </AnimatePresence>

      {/* Footer */}
      <Footer />

      {/* Scroll to Top Button */}
      <ScrollToTopButton threshold={400} />
    </div>
  );
};

export default MainLayout;
